import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from './GlobalContext';
import { useAuth } from './AuthContext';
import { Mode } from 'types/modes';
import { CollabErrorMap } from 'types/CollabErrorMap';
import { FileItem } from '../types/files';
import { API_ENDPOINTS } from '../config';
import * as Y from 'yjs';

interface SessionUser {
  userId: string;
  name: string;
  color: string;
  isHost?: boolean;
}

interface OpenDocumentUser {
  userId: string;
  userName: string;
  color: string;
}

interface OpenDocument {
  content: string;
  users: OpenDocumentUser[];
}

interface ActiveUserDocument {
  name: string;
  activeFilePath: string | null;
}

type CollabErrorKey = Mode;


interface CollabContextType {

  // ONLY HERE : ONLY COLLAB
  // PRIVATE STATES
  sessionId: string | null;
  users: SessionUser[];
  connection: signalR.HubConnection | null;
  collabErrors: CollabErrorMap;
  collabLoading: boolean;
  //

  // PRIVATE FUNCTIONS
  joinSession: (sessionId: string) => Promise<boolean>;
  hostSession: () => Promise<boolean>;
  leaveSession: () => Promise<void>;

  sendUpdatedFileTreeToBackend: () => Promise<void>;
  requestFileTreeFromHost: () => void;


  openDocuments: { [filePath: string]: OpenDocument };
  setOpenDocuments: React.Dispatch<React.SetStateAction<{ [filePath: string]: OpenDocument }>>;
  activeUserDocuments: ActiveUserDocument[];
  setActiveUserDocuments: React.Dispatch<React.SetStateAction<ActiveUserDocument[]>>;

  // PATH MAPPING (Virtual → Real)
  getRealPath: (virtualPath: string) => string | null;
}

const CollabContext = createContext<CollabContextType | undefined>(undefined);

export const CollabProvider = ({ children }: { children: ReactNode }) => {
  const {
    mode,
    setMode,
    projectRootPath,
    setProjectRootPathAndProjectName,
    projectName,
    fileTree,
    setFileTree,
    setVirtualFileTree,
    setActiveTab
  } = useGlobalContext();

  const { user } = useAuth();
  // Get username from authenticated user
  const username = user?.name || '';

  const userIdRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [users, setUsers] = useState<SessionUser[]>([]);

  
  // Virtual to Real path mapping (client-side only)
  const pathMappingRef = useRef<Map<string, string>>(new Map());

  const [collabLoading, setCollabLoading] = useState<boolean>(false);

  const { t } = useTranslation();

  const [openDocuments, setOpenDocuments] = useState<{ [filePath: string]: OpenDocument }>({});
  const [activeUserDocuments, setActiveUserDocuments] = useState<ActiveUserDocument[]>([]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  const [collabErrors, setCollabErrors] = useState<CollabErrorMap>({
    [Mode.Host]: null,
    [Mode.Join]: null,
    [Mode.Local]: null
  });

  // Prevent concurrent join attempts (double-click, rapid submit)
  const joinInFlightRef = useRef(false);

  // const knownParticipantsRef = useRef<Set<string>>(new Set());

  const setCollabError = (key: CollabErrorKey, message: string | null) => {
    setCollabErrors(prev => ({ ...prev, [key]: message }));
  };

  // useEffect(() => {
  //   const conn = connection
  //   const session = sessionId;
  //   const me = currentPrivateUserDataRef.current;
  //   const identity = identityKeysRef.current;
  //   const senderKey = localSenderKeyRef.current;

  //   if (!conn || !session || !me || !identity || !senderKey) {
  //     return; // not ready yet
  //   }

  //   const newUserIds = new Set(Object.keys(participants));
  //   const knownUserIds = knownParticipantsRef.current;

  //   const myUserId = me.user.id;

  //   // Find new OTHER users (exclude myself)
  //   const newOtherUsers: string[] = [];
  //   newUserIds.forEach((userId) => {
  //     if (!knownUserIds.has(userId) && userId !== myUserId) {
  //       newOtherUsers.push(userId);
  //     }
  //   });

  //   if (newOtherUsers.length > 0) {
  //     console.log("[SenderKey] New other users detected:", newOtherUsers);

  //     announceSenderKeyToSession(conn, session)
  //       .catch(err =>
  //         console.error("[SenderKey] announceSenderKeyToSession failed:", err)
  //       );
  //   }

  //   // Detect USERS THAT LEFT (optional for your use case)
  //   const removedUsers: string[] = [];
  //   knownUserIds.forEach(userId => {
  //     if (!newUserIds.has(userId)) {
  //       removedUsers.push(userId);
  //     }
  //   })

  //   if (removedUsers.length > 0) {
  //     console.log("[SenderKey] Users left:", removedUsers);
  //     // OPTIONAL: handle leave cleanup (not needed now)
  //   }

  //   // Update known participants to reflect latest state
  //   knownParticipantsRef.current = newUserIds;

  // }, [participants, connection, sessionId, localSenderKey]);


  // const announceSenderKeyToSession = async (conn: signalR.HubConnection, sessionId: string) => {
    
  //   console.log("Inside announceSenderKeyToSession");
  //   console.log("participantsRef.current : ", participantsRef.current);

  //   if (!conn || !sessionId || !currentPrivateUserDataRef.current) {
  //     if(!conn){
  //       console.log("No connection; cannot announce sender key");
  //     }
  //     if(!sessionId){
  //       console.log("No sessionId; cannot announce sender key");
  //     }
  //     if(!currentPrivateUserDataRef.current){
  //       console.log("No current private user data; cannot announce sender key");
  //     }

  //     return;

  //   }

  //   if (!localSenderKeyRef.current) {
  //     console.log("No local sender key to announce");
  //     return;
  //   }

  //   if (!identityKeysRef.current) {
  //     console.error("Cannot announce sender key: identity keys not available");
  //     return;
  //   }

    

  //   const myUserId = currentPrivateUserDataRef.current.user.id;
    
  //   for (const u of Object.values(participantsRef.current)){
      
  //     if (u.id === myUserId)
  //     {
  //       console
  //       continue;
  //     } 
  //     if(u.id !== myUserId){
  //       console.log("to User : ", u.username);
  //     }

  //     // Recipient must have X25519 public key
  //     if (!u.x25519PublicKey) {
  //       console.warn(
  //         `[SenderKey] Skipping announcement to ${u.username}: no X25519 public key`
  //       );
  //       continue;

  //       }

  //       try {
  //         // chainKey is stored as base64 string, convert to bytes for wrapSenderKey
  //         const senderChainKeyBytes = fromBase64(localSenderKeyRef.current.chainKey);
  //         const recipientX25519Bytes = fromBase64(u.x25519PublicKey);
        
  //         const publicUserData = sessionUserDataToTransmittable(currentPrivateUserDataRef.current);
          
  //         // Wrap sender key with X25519 + AES-GCM + Ed25519 signature
  //         const { nonce, encryptedKey, signature } = await wrapSenderKey(
  //           senderChainKeyBytes,
  //           identityKeysRef.current.x25519PrivateKey,
  //           recipientX25519Bytes,
  //           identityKeysRef.current.ed25519PrivateKey,
  //           sessionId,
  //           publicUserData.id,
  //           u.id,
  //           localSenderKeyRef.current.senderKeyId
  //         );
          
  //         const dto: SenderKeyAnnouncement = {
  //           sessionId: sessionId,
  //           senderUserId: publicUserData.id,
  //           recipientUserId: u.id,
  //           senderKeyId: localSenderKeyRef.current.senderKeyId,
  //           nonce: toBase64(nonce),
  //           encryptedSenderRootKey: toBase64(encryptedKey),
  //           signature: toBase64(signature),
  //         };

  //         const myUsername = currentPrivateUserDataRef.current.user.name;

  //         console.log(
  //         "[SenderKey] sent encrypted announcement",
  //         "room",
  //         dto.sessionId,
  //         "from",
  //         "(",
  //         myUsername,
  //         ")",
  //         myUserId,
  //         "to",
  //         u.id,
  //         "(",
  //         u.username,
  //         ")",
  //         "senderKeyId:",
  //         dto.senderKeyId
  //       );

  //         await conn.invoke("AnnounceSenderKey", dto);
  //       } 
  //       catch (err) {
  //         console.error(`AnnounceSenderKey failed for user ${u.username}:`, err);
  //       }

  //   }
  // };

  // const handleReceiveSenderKeyAnnouncement = async (dto: SenderKeyAnnouncement) => {
  //   console.log(
  //     "[SenderKey] received encrypted announcement",
  //     "session",
  //     dto.sessionId,
  //     "from",
  //     dto.senderUserId,
  //     "to",
  //     dto.recipientUserId,
  //     "senderKeyId:",
  //     dto.senderKeyId
  //   );

  //   try {
  //     // 1) Need identity + current user (from refs)
  //     const keys = identityKeysRef.current;
  //     const me = currentPrivateUserDataRef.current;

  //     if (!keys) {
  //       console.error("[SenderKey] Cannot unwrap: identity keys not available");
  //       return;
  //     }

  //     if (!me) {
  //       console.error("[SenderKey] Cannot unwrap: current user not available");
  //       return;
  //     }

  //     const myUserId = me.user.id;

  //     // 2) If this message is NOT for me, ignore it
  //     if (dto.recipientUserId !== myUserId) {
  //       // Not my sender key; just ignore
  //       return;
  //     }

  //     // 3) Lookup sender's public keys from participants (via ref)
  //     const sender = participantsRef.current[dto.senderUserId];
  //     if (!sender) {
  //       console.error(
  //         `[SenderKey] Cannot unwrap: sender ${dto.senderUserId} not found in participants`
  //       );
  //       return;
  //     }

  //     if (!sender.x25519PublicKey) {
  //       console.error(
  //         `[SenderKey] Cannot unwrap: sender ${dto.senderUserId} has no X25519 public key`
  //       );
  //       return;
  //     }

  //     if (!sender.ed25519PublicKey) {
  //       console.error(
  //         `[SenderKey] Cannot unwrap: sender ${dto.senderUserId} has no Ed25519 public key`
  //       );
  //       return;
  //     }

  //     // 4) Decode everything from base64
  //     const encryptedKeyBytes = fromBase64(dto.encryptedSenderRootKey);
  //     const nonceBytes = fromBase64(dto.nonce);
  //     const signatureBytes = fromBase64(dto.signature);
  //     const senderX25519Bytes = fromBase64(sender.x25519PublicKey);
  //     const senderEd25519Bytes = fromBase64(sender.ed25519PublicKey);

  //     // 5) Unwrap sender key (ECDH + HKDF + AES-GCM + Ed25519 verify)
  //     const decryptedChainKeyBytes = await unwrapSenderKey(
  //       encryptedKeyBytes,
  //       nonceBytes,
  //       signatureBytes,
  //       senderX25519Bytes,
  //       senderEd25519Bytes,
  //       keys.x25519PrivateKey,
  //       dto.sessionId,      // context / AAD
  //       dto.senderUserId,   // sender
  //       myUserId,           // recipient (current user)
  //       dto.senderKeyId
  //     );

  //     const decryptedChainKey = toBase64(decryptedChainKeyBytes);

  //     console.log(
  //       "[SenderKey] successfully unwrapped and verified sender key from",
  //       sender.username ?? dto.senderUserId
  //     );

  //     // 6) Store sender key for this sender
  //     // Create independent copies to avoid shared references
  //     const chainKeyCopy = new Uint8Array(decryptedChainKeyBytes);
  //     const ed25519PublicKeyCopy = new Uint8Array(senderEd25519Bytes);
      
  //     const remoteSenderKey: RemoteSenderKeyState = {
  //       sessionId: dto.sessionId,
  //       filePath: dto.filePath || '', // File path from announcement
  //       senderUserId: dto.senderUserId,
  //       senderKeyId: dto.senderKeyId,
  //       chainKey: chainKeyCopy, // ✨ Independent copy
  //       lastMessageIndex: -1,
  //       ed25519PublicKey: ed25519PublicKeyCopy // ✨ Independent copy
  //     };

  //     // If filePath is specified, store in file-room keys
  //     if (dto.filePath) {
  //       console.log(`[FileRoom] Storing remote sender key for file: ${dto.filePath}`);
  //       updateFileRoomRemoteSenderKey(dto.filePath, dto.senderUserId, remoteSenderKey);
  //     } else {
  //       // Legacy: store in global remote sender keys (for backward compatibility)
  //       console.log('[SenderKey] Storing in global remote sender keys (legacy)');
  //       setRemoteSenderKeys((prev) => ({
  //         ...prev,
  //         [dto.senderUserId]: {
  //           ...remoteSenderKey,
  //           chainKey: decryptedChainKey, // Store as base64 string for legacy
  //           ed25519PublicKey: sender.ed25519PublicKey // Store as base64 string for legacy
  //         } as any,
  //       }));
  //     }
  //   } catch (err) {
  //     console.error("[SenderKey] Failed to unwrap sender key:", err);
  //   }
  // };

  const sendUpdatedFileTreeToBackend = async () => {
    if (mode === Mode.Host && connection && sessionId) {
      try {
        await connection.invoke('UpdateFileTree', sessionId, projectName, fileTree, projectRootPath);
      } catch (err) {
        console.error('Failed to send updated file tree:', err);
      }
    }
  };

  const requestFileTreeFromHost = () => {
    if (mode !== Mode.Join || !connection || !sessionId) return;
    connection.invoke('RequestFileTree', sessionId);
  };

  const ensureUniqueUsers = (list: SessionUser[]) => {
    const map = new Map<string, SessionUser>();
    list.forEach(u => { if (!map.has(u.userId)) map.set(u.userId, u); });
    return Array.from(map.values());
  };

  const joinSession = async (sessionId: string) => {
    if (joinInFlightRef.current || collabLoading || connection) {
      return false;
    }
    joinInFlightRef.current = true;
    setCollabLoading(true);
    setCollabError(Mode.Join, null);
    try {
      

      const conn = new signalR.HubConnectionBuilder()
        .withUrl(API_ENDPOINTS.COLLAB.HUB)
        .withAutomaticReconnect()
        .build();
      await conn.start();

      const exists = await conn.invoke('CheckSessionExists', sessionId);
      if (!exists) {
        setCollabError(Mode.Join, t('Session Id is incorrect.'));
        setCollabLoading(false);
        joinInFlightRef.current = false;
        await conn.stop();
        return false;
      }
      conn.on('ReceiveSessionUsers', (userList: { userId: string, name: string; color: string; isHost?: boolean }[]) => {
        setUsers(ensureUniqueUsers(userList));
      });
      conn.on('ReceiveColor', (color: string) => {      
        const myUserId = userIdRef.current;
        if (!myUserId) return; // not logged in / not ready
        setUsers(prev =>
          ensureUniqueUsers(
            prev.some(u => u.userId === myUserId)
              ? prev.map(u => (u.userId === myUserId ? { ...u, name: username, color } : u))
              : [...prev, { userId: myUserId, name: username, color, isHost: false }]
          )
        );
      });
      conn.on('UserJoinedSession', async (userId: string,userName: string, color: string) => {
        setUsers(prev => ensureUniqueUsers(prev.some(u => u.userId === userId) ? prev : [...prev, { userId: userId, name: userName, color, isHost: false }]));
        
        // E2EE: Re-announce my public keys to the new joiner
        console.log("[JOIN] 📤 Re-announcing my keys to new user:", userName);
      });
      conn.on('UserLeftSession', (userId: string, userName: string) => {
        setUsers(prev => prev.filter(u => u.userId !== userId));
      });
      conn.on('SessionEnded', () => {
        leaveSession();
        setProjectRootPathAndProjectName(null);
      });
      conn.on('JoinSessionFailed', () => {
        setCollabError(Mode.Join, t('collab.joinSessionFailed'));
        setCollabLoading(false);
        setConnection(null);
        setSessionId(null);
      });
      conn.on('ReceiveFileTree', (receivedFileTree: FileItem[]) => {
        setVirtualFileTree(receivedFileTree);
      });


      await conn.invoke('JoinSession', sessionId,userIdRef.current, username);
      setConnection(conn);
      setSessionId(sessionId);
      setMode(Mode.Join);
      setProjectRootPathAndProjectName(sessionId);
      
      await conn.invoke('GetSessionUsers');
      await conn.invoke('RequestActiveUserDocuments', sessionId);
      setCollabLoading(false);
      joinInFlightRef.current = false;
      return true;
    } catch (err) {
      setCollabError(Mode.Join, ('collab.joinSessionFailed'));
      setCollabLoading(false);
      setConnection(null);
      setSessionId(null);
      joinInFlightRef.current = false;
      return false;
    }
  };

  const hostSession = async () => {
    console.log("Hosting session...");
    setCollabLoading(true);
    setCollabError(Mode.Host, null);
    try {

      const conn = new signalR.HubConnectionBuilder()
        .withUrl(API_ENDPOINTS.COLLAB.HUB)
        .withAutomaticReconnect()
        .build();
      await conn.start();
      setConnection(conn);


      console.log("Connection started.");
      
      // STEP 1: Generate client-side sessionId
      const clientSessionId = crypto.randomUUID();
      console.log("Generated client sessionId:", clientSessionId);
      
      // STEP 2: Virtualize file tree with client sessionId
      console.log("Virtualizing file tree...");
      const { virtualizeFileTree } = await import('../utils/pathUtils');
      const { virtualTree, pathMapping } = virtualizeFileTree(
        fileTree,
        clientSessionId,
        projectName || 'Project',
        projectRootPath || ''
      );
      
      // Store path mapping for later use
      pathMappingRef.current = pathMapping;
      console.log(`Path mapping created with ${pathMapping.size} entries`);
      
      conn.on('ReceiveFileTree', (receivedFileTree: FileItem[]) => {
        //printFileTree(receivedFileTree, ' ');
        console.log('[Host Mode] Received file tree from backend, setting virtualFileTree');
        setVirtualFileTree(receivedFileTree);
      });
      console.trace(projectName)
      // Burada projectPath context'teki state olarak geliyor!

      // STEP 3: Send to backend with client sessionId
      console.log("Invoking StartSession with client sessionId and virtualized tree...");
      const backendSessionId = await conn.invoke<string>(
        'StartSession',
        userIdRef.current,
        username,
        clientSessionId, // Send client sessionId so backend uses it
        virtualTree);
      
      console.log("Backend confirmed sessionId:", backendSessionId);
      console.log("SessionIds match:", clientSessionId === backendSessionId);
      setSessionId(backendSessionId);
      setMode(Mode.Host);

      let myColor = '#157c96';
      conn.on(
        'ReceiveSessionUsers',
        (userList: { userId: string; name: string; color: string; isHost?: boolean }[]) => {
          setUsers(ensureUniqueUsers(userList));
        }
      );
      conn.on('ReceiveColor', (color: string) => {
        
        myColor = color;
        
        const myUserId = userIdRef.current;
        if (!myUserId) return; // not logged in / not ready

        setUsers(prev =>
          ensureUniqueUsers(
            prev.some(u => u.userId === myUserId)
              ? prev.map(u => (u.userId === myUserId ? { ...u, name: username, color } : u))
              : [...prev, { userId: myUserId, name: username, color, isHost: false }]
          )
        );
      });
      conn.on('UserJoinedSession', async (userId: string,userName: string, color: string) => {
        setUsers(prev => prev.some(u => u.userId === userId) ? prev : [...prev, { userId: userId, name: userName, color, isHost: false }]);
        
        // E2EE: Re-announce my public keys to the new joiner
        console.log("[HOST] 📤 Re-announcing my keys to new user:", userName);
       // await conn.invoke('AnnouncePublicKeys', backendSessionId, publicUserData);
      });
      
      conn.on('UserLeftSession', (userId: string, userName: string) => {
        setUsers(prev => prev.filter(u => u.userId !== userId));
      });
      
      await conn.invoke('GetSessionUsers');
      await conn.invoke('RequestActiveUserDocuments', backendSessionId);
      setCollabLoading(false);
      return true;
    } catch (err) {
      setCollabError(Mode.Host, ('Failed to start session'));
      setCollabLoading(false);
      setConnection(null);
      setSessionId(null);
      return false;
    }
  };

  const leaveSession = async () => {
    if (connection) {
      try {
        connection.off('ReceiveSessionUsers');
        connection.off('ReceiveColor');
        connection.off('UserJoinedSession');
        connection.off('UserLeftSession');
        connection.off('ReceivePublicKeys');
        await connection.stop();
      } catch { }
    }
    setConnection(null);
    setSessionId(null);
    setUsers([]);
    
    // Clean up E2EE state
    
    setCollabError(mode, null);
    setCollabLoading(false);
    
    setVirtualFileTree([]);

    pathMappingRef.current.clear(); // optional but recommended

    // Close active tab when leaving session
    setActiveTab(null);
    
    if (mode === Mode.Join && projectRootPath) {
      setProjectRootPathAndProjectName(null);
    }
    setMode(Mode.Local);
  };

// const getOrCreateFileRoomKeys = (filePath: string): FileRoomKeyState => {
//     console.log('════════════════════════════════════════════════════════════');
//     console.log('🔑 [getOrCreateFileRoomKeys] CALLED');
//     console.log('════════════════════════════════════════════════════════════');
//     console.log('  📁 File Path:', filePath);
    
//     // safe access to refs
//     let room = fileRoomKeysRef.current.get(filePath);
//     if (room) {
//       console.log('  ♻️  Existing room found for file');
//       console.log('    - Local Chain Key Type:', typeof room.local.chainKey);
//       console.log('    - Local Chain Key Length:', room.local.chainKey?.length);
//       console.log('    - Local Message Index:', room.local.nextMessageIndex);
//       console.log('    - Remote Keys Count:', room.remote.size);
//       console.log('════════════════════════════════════════════════════════════');
//       return room;
//     }

//     console.log('  🆕 Creating NEW room for file');

//     // 1️⃣ Create INDEPENDENT LOCAL sender key for this file room
//     // IMPORTANT: Do NOT use localSenderKeyRef.current directly as it's shared!
//     // Each file room needs its own independent sender key state
//     const baseLocal = localSenderKeyRef.current;
//     console.log('  📊 Base Local Sender Key:');
//     console.log('    - Exists:', !!baseLocal);
//     if (baseLocal) {
//       console.log('    - Session ID:', baseLocal.sessionId);
//       console.log('    - User ID:', baseLocal.userId);
//       console.log('    - Sender Key ID:', baseLocal.senderKeyId);
//       console.log('    - Chain Key Type:', typeof baseLocal.chainKey);
//       console.log('    - Chain Key Length:', typeof baseLocal.chainKey === 'string' ? baseLocal.chainKey.length : baseLocal.chainKey?.length);
//       console.log('    - Next Message Index (IGNORED for new room):', baseLocal.nextMessageIndex);
//     }
    
//     if (!baseLocal) {
//       console.error('❌ [getOrCreateFileRoomKeys] localSenderKey not ready!');
//       throw new Error("localSenderKey not ready");
//     }

//     // Convert chain key to Uint8Array
//     const chainKeyConverted = typeof baseLocal.chainKey === 'string' ? fromBase64(baseLocal.chainKey) : baseLocal.chainKey;
//     console.log('  🔄 Chain Key Conversion:');
//     console.log('    - Before Type:', typeof baseLocal.chainKey);
//     console.log('    - After Type:', typeof chainKeyConverted);
//     console.log('    - After instanceof Uint8Array:', chainKeyConverted instanceof Uint8Array);
//     console.log('    - After Length:', chainKeyConverted?.length);

//     // Convert ed25519PrivateKey to Uint8Array
//     const ed25519PrivateKeyConverted = typeof baseLocal.ed25519PrivateKey === 'string' 
//       ? fromBase64(baseLocal.ed25519PrivateKey) 
//       : baseLocal.ed25519PrivateKey;
//     console.log('  🔄 Ed25519 Private Key Conversion:');
//     console.log('    - Before Type:', typeof baseLocal.ed25519PrivateKey);
//     console.log('    - After Type:', typeof ed25519PrivateKeyConverted);
//     console.log('    - After instanceof Uint8Array:', ed25519PrivateKeyConverted instanceof Uint8Array);
//     console.log('    - After Length:', ed25519PrivateKeyConverted?.length);

//     // 🚨 CRITICAL: Create COPY of chainKey to avoid shared reference!
//     // If we just copy the Uint8Array reference, all rooms will share the same chainKey
//     // and it will get mutated by encryptAndSignYjsUpdate
//     const chainKeyCopy = new Uint8Array(chainKeyConverted);
//     console.log('  📋 Created independent chain key copy');
//     console.log('    - Original === Copy:', chainKeyConverted === chainKeyCopy);
//     console.log('    - Copy Length:', chainKeyCopy.length);

//     // 🔑 CRITICAL: Store root chain key separately for snapshots
//     // This key will NEVER be ratcheted and always stays at the initial state
//     // Used exclusively for snapshot encryption to ensure joiners can decrypt
//     const rootChainKeyCopy = new Uint8Array(chainKeyConverted);
//     console.log('  🔐 Created root chain key copy for snapshots');
//     console.log('    - Root Key Length:', rootChainKeyCopy.length);

//     const local: SenderKeyState = {
//       sessionId: baseLocal.sessionId,
//       filePath: filePath,
//       userId: baseLocal.userId,
//       senderKeyId: baseLocal.senderKeyId,
//       chainKey: chainKeyCopy, // ✨ Independent copy! (will be ratcheted)
//       ed25519PrivateKey: ed25519PrivateKeyConverted,
//       nextMessageIndex: 0, // ✨ Always start from 0 for new file room
//     };

//     console.log('  ✅ Created Local Sender Key:');
//     console.log('    - File Path:', local.filePath);
//     console.log('    - Chain Key Type:', typeof local.chainKey);
//     console.log('    - Chain Key instanceof Uint8Array:', local.chainKey instanceof Uint8Array);
//     console.log('    - Chain Key Length:', local.chainKey?.length);
//     console.log('    - Ed25519 Private Key Type:', typeof local.ed25519PrivateKey);
//     console.log('    - Ed25519 Private Key Length:', local.ed25519PrivateKey?.length);
//     console.log('    - Next Message Index:', local.nextMessageIndex);

//     // 2️⃣ Create INDEPENDENT REMOTE sender keys for this file room
//     // IMPORTANT: Create copies to avoid shared references between rooms
//     const remote = new Map<string, RemoteSenderKeyState>();


//     for (const [userId, k] of Object.entries(remoteSenderKeysRef.current)) {
//       console.log(`    - Processing User ${userId}:`);
//       console.log(`      - Sender Key ID:`, k.senderKeyId);
//       console.log(`      - Chain Key Type:`, typeof k.chainKey);
      
//       // Convert to Uint8Array and create independent copy
//       const chainKeyBytes = ensureBytes(k.chainKey);
//       const chainKeyCopy = new Uint8Array(chainKeyBytes); // ✨ Independent copy!
      
//       const ed25519PublicKeyBytes = ensureBytes(k.ed25519PublicKey);
//       const ed25519PublicKeyCopy = new Uint8Array(ed25519PublicKeyBytes); // ✨ Independent copy!
      
//       const remoteKey: RemoteSenderKeyState = {
//         sessionId: k.sessionId,
//         filePath: filePath,
//         senderUserId: userId,
//         senderKeyId: k.senderKeyId,
//         chainKey: chainKeyCopy, // ✨ Independent copy!
//         lastMessageIndex: -1, // ✨ Always start from -1 for new file room
//         ed25519PublicKey: ed25519PublicKeyCopy, // ✨ Independent copy!
//       };
      
//       remote.set(userId, remoteKey);
//       console.log(`      - Added to remote map with independent copies`);
//     }

//     room = { local, remote, rootChainKey: rootChainKeyCopy };
//     fileRoomKeysRef.current.set(filePath, room);
//     console.log('  💾 Room saved to fileRoomKeysRef');
//     console.log('  🔐 Root chain key stored for snapshot encryption');
//     console.log('  ✅ File room created successfully!');
//     console.log('════════════════════════════════════════════════════════════');
//     return room;
// };

// const updateFileRoomRemoteSenderKey = (filePath: string, userId: string, remoteSenderKey: RemoteSenderKeyState) => {
//   // Get or create file room keys if not exists
//   const room = getOrCreateFileRoomKeys(filePath);
//   room.remote.set(userId, remoteSenderKey);
//   console.log(`[FileRoom] Updated remote sender key for user ${userId} in file: ${filePath}`);
// };

// const clearFileRoomKeys = (filePath: string) => {
//   const deleted = fileRoomKeysRef.current.delete(filePath);
//   if (deleted) {
//     console.log(`[FileRoom] ✅ Cleared file room keys for: ${filePath}`);
//   } else {
//     console.log(`[FileRoom] ⚠️ No keys to clear for: ${filePath}`);
//   }
// };

// const announceSenderKeyForFile = async (filePath: string) => {
//   const conn = connection;
//   const me = currentPrivateUserDataRef.current;
//   const identity = identityKeysRef.current;

//   const sid = sessionIdRef.current;

//   console.log("[FileRoom][announce] prereq snapshot", {
//     sessionId: sid,
//     hasConnection: !!connection,
//     connectionState: connection?.state,
//     hasIdentityKeys: !!identityKeys,
//     hasCurrentUser: !!currentPrivateUserData,
//     userId: currentPrivateUserData?.user?.id,
//     userName: currentPrivateUserData?.user?.name,
//   });

//   if (!conn || !sid || !me || !identity) {
//     console.error("[FileRoom] Cannot announce: missing prerequisites");
//     return;
//   }

//   const fileRoomKeys = getOrCreateFileRoomKeys(filePath);
//   const myUserId = me.user.id;

//   console.log(`[FileRoom] Announcing sender key for file: ${filePath}`);
//   console.log(`[FileRoom] 🔐 Using ROOT chain key for announcement (not ratcheted key)`);
//   console.log(`  - Root Chain Key Length: ${fileRoomKeys.rootChainKey.length}`);
//   console.log(`  - Current Local Sender Key Index: ${fileRoomKeys.local.nextMessageIndex} (NOT announced)`);

//   for (const u of Object.values(participantsRef.current)) {
//     if (u.id === myUserId) continue;

//     if (!u.x25519PublicKey) {
//       console.warn(`[FileRoom] Skipping ${u.username}: no X25519 key`);
//       continue;
//     }

//     try {
//       // 🔐 CRITICAL FIX: Always announce the ROOT chain key, not the ratcheted one
//       // This ensures new joiners receive the initial key state for snapshot decryption
//       const senderChainKeyBytes = fileRoomKeys.rootChainKey; // ✨ Use root key
//       const recipientX25519Bytes = fromBase64(u.x25519PublicKey);
//       const publicUserData = sessionUserDataToTransmittable(me);

//       const { nonce, encryptedKey, signature } = await wrapSenderKey(
//         senderChainKeyBytes,
//         identity.x25519PrivateKey,
//         recipientX25519Bytes,
//         identity.ed25519PrivateKey,
//         sid,
//         publicUserData.id,
//         u.id,
//         fileRoomKeys.local.senderKeyId
//       );

//       const dto: SenderKeyAnnouncement = {
//         sessionId: sid,
//         senderUserId: publicUserData.id,
//         recipientUserId: u.id,
//         senderKeyId: fileRoomKeys.local.senderKeyId,
//         nonce: toBase64(nonce),
//         encryptedSenderRootKey: toBase64(encryptedKey),
//         signature: toBase64(signature),
//         filePath: filePath, // ✨ File-room based announcement
//       };

//       console.log(`[FileRoom] Announcing to ${u.username} for file: ${filePath}`);
//       await conn.invoke("AnnounceSenderKey", dto);
//     } catch (err) {
//       console.error(`[FileRoom] Failed to announce to ${u.username}:`, err);
//     }
//   }
// };


  useEffect(() => {
    if (!connection) return;
    // connection.on(
    //   'UserJoinedDocument',
    //   (userId: string, userName: string, color: string, filePath: string) => {
    //     setOpenDocuments(prev => {
    //       const prevUsers = prev[filePath]?.users || [];

    //       const users = prevUsers.some(u => u.userId === userId)
    //         ? prevUsers.map(u => (u.userId === userId ? { ...u, userName, color } : u))
    //         : [...prevUsers, { userId, userName, color }];

    //       return {
    //         ...prev,
    //         [filePath]: {
    //           ...(prev[filePath] || { content: '' }),
    //           users,
    //         },
    //       };
    //     });

    //     console.log("[FileRoom][UserJoinedDocument] about to announce", {
    //       filePath,
    //       mode,
    //       sessionId,
    //       hasConnection: !!connection,
    //       connectionState: connection?.state,
    //     });
    //     announceSenderKeyForFile(filePath).catch(err => {
    //       console.error('[FileRoom] Failed to announce sender key on user join:', err);
    //     });
    //   }
    // );
    connection.on('UserLeftDocument', (userId: string, filePath: string) => {
      
      setOpenDocuments(prev => ({
        ...prev,
        [filePath]: {
          ...(prev[filePath] || { content: '', users: [] }),
          users: (prev[filePath]?.users || []).filter(u => u.userId !== userId),
        },
      }));
    });
    const onReceiveActiveUserDocuments = (userDocs: ActiveUserDocument[]) => {
      setActiveUserDocuments(userDocs);
    };

    connection.on('ReceiveActiveUserDocuments', onReceiveActiveUserDocuments);
    return () => {
      connection.off('UserJoinedDocument');
      connection.off('UserLeftDocument');
      connection.off('ReceiveActiveUserDocuments', onReceiveActiveUserDocuments);
    };
  }, [connection]);

  // Helper: Get real path from virtual path
  const getRealPath = (virtualPath: string): string | null => {
    return pathMappingRef.current.get(virtualPath) || null;
  };

  return (
    <CollabContext.Provider value={{
      sessionId,
      users,
      connection,
      collabLoading,
      joinSession,
      hostSession,
      leaveSession,
      sendUpdatedFileTreeToBackend,
      requestFileTreeFromHost,
      collabErrors,
      //
      openDocuments,
      setOpenDocuments,
      activeUserDocuments,
      setActiveUserDocuments,
      
      
      // PATH MAPPING
      getRealPath
    }}>
      {children}
    </CollabContext.Provider>
  );
};

export const useCollab = () => {
  const ctx = useContext(CollabContext);
  if (!ctx) throw new Error('useCollab must be used within a CollabProvider');
  return ctx;
};